import { Supabase } from "../Utils/supabase.config";
import { canPostAnnouncement } from "./communityService";

// Test function to check Supabase connection and tables
export const testSupabaseConnection = async () => {
  try {
    // Test basic connection
    const { data: authData, error: authError } = await Supabase.auth.getSession();
    
    // Test if polls table exists and get its structure
    const { data: tableTest, error: tableError } = await Supabase
      .from('polls')
      .select('*')
      .limit(1);
    
    // Also test what columns exist by trying different column names
    const { data: columnTest, error: columnError } = await Supabase
      .from('polls')
      .select('userid, created_at')
      .limit(1);
    
    return { success: !tableError, error: tableError };
  } catch (error) {
    return { success: false, error };
  }
};

// Create a new poll
export const createPoll = async (pollData) => {
  try {
    // Check if user can post announcements in this community
    if (pollData.is_announcement && pollData.community_id) {
      const canPost = await canPostAnnouncement(pollData.userId, pollData.community_id);
      if (!canPost.success || !canPost.data) {
        return { success: false, msg: "You don't have permission to post announcements in this community" };
      }
    }

    // Create poll
    const { data: poll, error: pollError } = await Supabase
      .from('polls')
      .insert([{
        question: pollData.question,
        description: pollData.description,
        userid: pollData.userId, // Use userid (lowercase) to match actual database schema
        community_id: pollData.community_id,
        poll_type: pollData.poll_type || 'regular',
        is_announcement: pollData.is_announcement || false,
        expires_at: pollData.expires_at,
        allow_multiple_choices: pollData.allow_multiple_choices || false,
        is_anonymous: pollData.is_anonymous || false
      }])
      .select()
      .single();

    if (pollError) {
      return { success: false, msg: "Could not create poll: " + pollError.message };
    }

    // Create poll options
    const options = pollData.options.map((option, index) => ({
      poll_id: poll.id,
      option_text: option,
      option_order: index + 1
    }));

    const { error: optionsError } = await Supabase
      .from('poll_options')
      .insert(options);

    if (optionsError) {
      // Rollback poll creation if options fail
      await Supabase.from('polls').delete().eq('id', poll.id);
      return { success: false, msg: "Could not create poll options" };
    }

    return { success: true, data: poll };
  } catch (error) {
    return { success: false, msg: "Could not create poll" };
  }
};

// Fetch polls with pagination
export const fetchPolls = async (limit = 10, userId = null, communityId = null) => {
  try {
    let query = Supabase
      .from('polls')
      .select(`
        *,
        user: users ( id, name, image ),
        community: communities ( name ),
        poll_options (*),
        poll_votes (count)
      `)
      .eq('hidden', false)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (userId) {
      query = query.eq('userid', userId);
    }

    if (communityId) {
      query = query.eq('community_id', communityId);
    }

    const { data, error } = await query;

    if (error) {
      return { success: false, msg: "Could not fetch polls" };
    }

    return { success: true, data: data || [] };
  } catch (error) {
    return { success: false, msg: "Could not fetch polls" };
  }
};

// Fetch polls with cursor-based pagination
export const fetchPollsWithCursor = async (limit = 10, userId = null, communityId = null, cursor = null) => {
  try {
    let query = Supabase
      .from('polls')
      .select(`
        *,
        user: users ( id, name, image ),
        community: communities ( name ),
        poll_options (*),
        poll_votes (count)
      `)
      .eq('hidden', false)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (cursor) {
      query = query.lt('created_at', cursor);
    }

    if (userId) {
      query = query.eq('userid', userId);
    }

    if (communityId) {
      query = query.eq('community_id', communityId);
    }

    const { data, error } = await query;
    
    if (error) {
      return { success: false, msg: `Could not fetch polls: ${error.message}` };
    }

    const nextCursor = data && data.length > 0 ? data[data.length - 1].created_at : null;
    const hasMore = data && data.length === limit;

    return {
      success: true,
      data: data || [],
      nextCursor,
      hasMore
    };
  } catch (error) {
    return { success: false, msg: `Could not fetch polls: ${error.message}` };
  }
};

// Fetch single poll with results
export const fetchPollDetails = async (pollId) => {
  try {
    const { data, error } = await Supabase
      .from('polls')
      .select(`
        *,
        user: users ( id, name, image ),
        community: communities ( name ),
        poll_options (*)
      `)
      .eq('id', pollId)
      .eq('hidden', false)
      .single();

    if (error) {
      return { success: false, msg: "Could not fetch poll" };
    }

    // Get poll results using the database function
    const { data: results, error: resultsError } = await Supabase
      .rpc('get_poll_results', { poll_uuid: pollId });

    if (resultsError) {
      console.warn('Could not fetch poll results:', resultsError);
    }

    return {
      success: true,
      data: {
        ...data,
        results: results || []
      }
    };
  } catch (error) {
    return { success: false, msg: "Could not fetch poll" };
  }
};

// Vote on a poll
export const voteOnPoll = async (pollId, optionIds, userId) => {
  try {
    // Check if poll allows multiple choices
    const { data: poll, error: pollError } = await Supabase
      .from('polls')
      .select('allow_multiple_choices')
      .eq('id', pollId)
      .single();

    if (pollError) {
      return { success: false, msg: "Poll not found" };
    }

    // If not multiple choices and user has already voted, remove existing votes
    if (!poll.allow_multiple_choices) {
      const { error: deleteError } = await Supabase
        .from('poll_votes')
        .delete()
        .eq('poll_id', pollId)
        .eq('user_id', userId);

      if (deleteError) {
        return { success: false, msg: "Could not update vote" };
      }
    }

    // Insert new votes
    const votes = optionIds.map(optionId => ({
      poll_id: pollId,
      poll_option_id: optionId,
      user_id: userId
    }));

    const { error: insertError } = await Supabase
      .from('poll_votes')
      .insert(votes);

    if (insertError) {
      return { success: false, msg: "Could not vote" };
    }

    return { success: true };
  } catch (error) {
    return { success: false, msg: "Could not vote" };
  }
};

// Remove vote from poll
export const removeVote = async (pollId, optionId, userId) => {
  try {
    const { error } = await Supabase
      .from('poll_votes')
      .delete()
      .eq('poll_id', pollId)
      .eq('poll_option_id', optionId)
      .eq('user_id', userId);

    if (error) {
      return { success: false, msg: "Could not remove vote" };
    }

    return { success: true };
  } catch (error) {
    return { success: false, msg: "Could not remove vote" };
  }
};

// Check if user has voted on a poll
export const hasUserVoted = async (pollId, userId) => {
  try {
    const { data, error } = await Supabase
      .rpc('has_user_voted', { poll_uuid: pollId, user_uuid: userId });

    if (error) {
      return { success: false, data: false };
    }

    return { success: true, data: data || false };
  } catch (error) {
    return { success: false, data: false };
  }
};

// Get user's votes for a poll
export const getUserVotes = async (pollId, userId) => {
  try {
    const { data, error } = await Supabase
      .from('poll_votes')
      .select('poll_option_id')
      .eq('poll_id', pollId)
      .eq('user_id', userId);

    if (error) {
      return { success: false, msg: "Could not fetch user votes" };
    }

    return {
      success: true,
      data: data.map(vote => vote.poll_option_id)
    };
  } catch (error) {
    return { success: false, msg: "Could not fetch user votes" };
  }
};

// Delete poll (creator only)
export const deletePoll = async (pollId, userId) => {
  try {
    const { error } = await Supabase
      .from('polls')
      .delete()
      .eq('id', pollId)
      .eq('userid', userId);

    if (error) {
      return { success: false, msg: "Could not delete poll" };
    }

    return { success: true };
  } catch (error) {
    return { success: false, msg: "Could not delete poll" };
  }
};

// Update poll (creator only)
export const updatePoll = async (pollId, updates, userId) => {
  try {
    const { data, error } = await Supabase
      .from('polls')
      .update(updates)
      .eq('id', pollId)
      .eq('userid', userId)
      .select()
      .single();

    if (error) {
      return { success: false, msg: "Could not update poll" };
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, msg: "Could not update poll" };
  }
};

// Fetch polls for a specific community
export const fetchCommunityPolls = async (communityId, limit = 20, offset = 0) => {
  try {
    const { data, error } = await Supabase
      .from('polls')
      .select(`
        *,
        user: users ( id, name, image ),
        community: communities ( name ),
        poll_options (*),
        poll_votes (count)
      `)
      .eq('community_id', communityId)
      .eq('hidden', false)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      return { success: false, error };
    }

    return { success: true, data: data || [] };
  } catch (err) {
    return { success: false, error: err };
  }
};
